import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { reverseAuthGuard } from './core/guards/reverse-auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { roleRedirectGuard } from './core/guards/role-redirect.guard';
import { hospitalAccessGuard } from './core/guards/hospital-access.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { adminGuard } from './core/guards/admin.guard';
import { pharmacyAdminGuard } from './core/guards/pharmacy-admin.guard';
import { matAccessGuard } from './core/guards/mat-access.guard';
import { userResolver } from './core/resolvers/user.resolver';
import { UserRole } from './core/interfaces/api.interface';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/auth/login/login').then(m => m.Login),
    canActivate: [reverseAuthGuard],
    pathMatch: 'full'
  },
  {
    // login-farmacias redirect target. No guards: the app initializer consumes the response.
    path: 'sso/callback',
    loadComponent: () => import('./pages/auth/sso-callback/sso-callback').then(m => m.SsoCallback)
  },
  {
    path: 'signup',
    loadComponent: () => import('./pages/auth/signup/signup').then(m => m.SignupComponent),
    canActivate: [reverseAuthGuard]
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/auth/reset-password/reset-password').then(m => m.ResetPasswordComponent),
    canActivate: [reverseAuthGuard]
  },
  {
    path: 'auth/confirm',
    loadComponent: () => import('./pages/auth/confirm/confirm').then(m => m.ConfirmComponent),
    canActivate: [reverseAuthGuard]
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home').then(m => m.HomeComponent),
    canActivate: [authGuard],
    children: [] // This route only exists for redirection
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard').then(m => m.DashboardComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'mat',
    loadComponent: () => import('./pages/mat/mat-patients').then(m => m.MatPatientsComponent),
    canActivate: [authGuard, matAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'mat/guide',
    loadComponent: () => import('./pages/mat/mat-guide/mat-guide').then(m => m.MatGuideComponent),
    canActivate: [authGuard, matAccessGuard]
  },
  {
    path: 'patients',
    loadComponent: () => import('./pages/patients/patients').then(m => m.PatientsComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'patients/:id',
    loadComponent: () => import('./pages/patients/patient-detail/patient-detail').then(m => m.PatientDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'patients/:id/mat',
    loadComponent: () => import('./pages/patients/mat-patient-detail/mat-patient-detail').then(m => m.MatPatientDetailComponent),
    canActivate: [authGuard, matAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'cases',
    loadComponent: () => import('./pages/cases/cases').then(m => m.Cases),
    canActivate: [authGuard, hospitalAccessGuard],
    canDeactivate: [unsavedChangesGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'cases/:id',
    loadComponent: () => import('./pages/cases/case-detail/case-detail').then(m => m.CaseDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'tasks',
    loadComponent: () => import('./pages/tasks/tasks').then(m => m.TasksComponent),
    canActivate: [authGuard],
    canDeactivate: [unsavedChangesGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'tasks/:id',
    loadComponent: () => import('./pages/tasks/task-detail/task-detail.component').then(m => m.TaskDetailComponent),
    canActivate: [authGuard],
    resolve: {
      user: userResolver
    }
  },
  /*{
    path: 'tracking',
    loadComponent: () => import('./pages/tracking/tracking').then(m => m.TrackingComponent),
    canActivate: [authGuard, hospitalAccessGuard]
  },*/
  {
    path: 'tracking',
    loadComponent: () => import('./pages/order-tracking/order-tracking').then(m => m.OrderTracking),
    canActivate: [authGuard, hospitalAccessGuard]
  },
  {
    path: 'contacts/:id',
    loadComponent: () => import('./pages/contacts/contact-detail/contact-detail').then(m => m.ContactDetail),
    canActivate: [authGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'profile',
    loadComponent: () => import('./pages/profile/profile').then(m => m.ProfileComponent),
    canActivate: [authGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'team',
    loadComponent: () => import('./pages/team/team').then(m => m.TeamComponent),
    canActivate: [authGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'pharmacy/accounts',
    loadComponent: () => import('./pages/pharmacy-accounts/pharmacy-accounts').then(m => m.PharmacyAccountsComponent),
    canActivate: [authGuard, pharmacyAdminGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'admin/users',
    loadComponent: () => import('./pages/users-table/users-table').then(m => m.UsersTableComponent),
    canActivate: [authGuard, adminGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'admin/farmacias-sync',
    loadComponent: () => import('./pages/farmacias-sync/farmacias-sync').then(m => m.FarmaciasSyncComponent),
    canActivate: [authGuard, adminGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'admin/sms',
    loadComponent: () => import('./pages/sms-usage/sms-usage').then(m => m.SmsUsageComponent),
    canActivate: [authGuard, adminGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'resources',
    loadComponent: () => import('./pages/resources/resources').then(m => m.ResourcesComponent),
    canActivate: [authGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'drug-reactions/:id',
    loadComponent: () => import('./pages/drug-reactions/drug-reaction-detail/drug-reaction-detail').then(m => m.DrugReactionDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'chronic-medications/:id',
    loadComponent: () => import('./pages/chronic-medications/chronic-medication-detail/chronic-medication-detail').then(m => m.ChronicMedicationDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'health-problems/:id',
    loadComponent: () => import('./pages/health-problems/health-problem-detail/health-problem-detail').then(m => m.HealthProblemDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'survey-forms/:id',
    loadComponent: () => import('./pages/survey-forms/survey-form-detail/survey-form-detail').then(m => m.SurveyFormDetailComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    path: 'survey-forms/:id/printable',
    loadComponent: () => import('./pages/survey-forms/survey-form-detail/survey-form-printable/survey-form-printable').then(m => m.SurveyFormPrintableComponent),
    canActivate: [authGuard, hospitalAccessGuard],
    resolve: {
      user: userResolver
    }
  },
  {
    // The cookie policy (Microsoft Clarity), public so the banner's "Saber mais" works before sign-in.
    path: 'cookies',
    loadComponent: () => import('./pages/cookies/cookies').then(m => m.CookiesPageComponent)
  },
  {
    path: '**',
    redirectTo: ''
  }
]; 